import { GlobeLock, Plus, Trash2 } from "lucide-react";
import { useCallback, useState } from "react";
import { Button } from "@/components/ui/atoms/button";
import { Input } from "@/components/ui/atoms/input";
import { Label } from "@/components/ui/atoms/label";
import { Switch } from "@/components/ui/atoms/switch";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/organisms/dialog";
import {
  addSearchEngine,
  getSearchEngines,
  removeSearchEngine,
  type SearchEngine,
  updateSearchEngine,
} from "@/lib/search-engine-config";
import { cn } from "@/lib/utils";

export function SearchSettings() {
  const [engines, setEngines] = useState(getSearchEngines);
  const [addOpen, setAddOpen] = useState(false);
  const [newEngine, setNewEngine] = useState({
    label: "",
    url: "",
    icon: "",
    privacyUrl: "",
    enabled: true,
  });

  const handleToggle = useCallback((id: string, enabled: boolean) => {
    updateSearchEngine(id, { enabled });
    setEngines(getSearchEngines());
  }, []);

  const handleDelete = useCallback((id: string) => {
    removeSearchEngine(id);
    setEngines(getSearchEngines());
  }, []);

  const handleAdd = useCallback(() => {
    if (!newEngine.label || !newEngine.url) return;
    if (!newEngine.url.includes("{query}")) return;
    addSearchEngine({
      label: newEngine.label,
      url: newEngine.url,
      icon: newEngine.icon || undefined,
      privacyUrl: newEngine.privacyUrl || undefined,
      enabled: newEngine.enabled,
    });
    setEngines(getSearchEngines());
    setNewEngine({ label: "", url: "", icon: "", privacyUrl: "", enabled: true });
    setAddOpen(false);
  }, [newEngine]);

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <div>
        <h2 className="text-lg font-semibold">Search Engines</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Choose which search engines are available. Add custom engines with your own search URL
          template.
        </p>
      </div>

      <div className="rounded-lg border divide-y">
        {engines.map((engine) => (
          <EngineRow
            key={engine.id}
            engine={engine}
            onToggle={(enabled) => handleToggle(engine.id, enabled)}
            onDelete={engine.isCustom ? () => handleDelete(engine.id) : undefined}
          />
        ))}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Custom engines are saved locally in your browser.
        </p>
        <Button variant="outline" size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="size-3.5" />
          Add search engine
        </Button>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogTitle>Add Custom Search Engine</DialogTitle>
          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input
                value={newEngine.label}
                onChange={(e) => setNewEngine((p) => ({ ...p, label: e.target.value }))}
                placeholder="e.g. Kagi"
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Search URL</Label>
              <Input
                value={newEngine.url}
                onChange={(e) => setNewEngine((p) => ({ ...p, url: e.target.value }))}
                placeholder="https://kagi.com/search?q={query}"
                className="h-9 font-mono text-xs"
              />
              <p className="text-[11px] text-muted-foreground">
                Use <code className="text-xs">{`{query}`}</code> as placeholder for the search term.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Favicon URL (optional)</Label>
              <Input
                value={newEngine.icon}
                onChange={(e) => setNewEngine((p) => ({ ...p, icon: e.target.value }))}
                placeholder="https://kagi.com/favicon.ico"
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Privacy Policy URL (optional)</Label>
              <Input
                value={newEngine.privacyUrl}
                onChange={(e) => setNewEngine((p) => ({ ...p, privacyUrl: e.target.value }))}
                placeholder="https://kagi.com/privacy"
                className="h-9"
              />
            </div>
            <Button
              onClick={handleAdd}
              className="w-full"
              disabled={!newEngine.label || !newEngine.url?.includes("{query}")}
            >
              Add engine
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EngineRow({
  engine,
  onToggle,
  onDelete,
}: {
  engine: SearchEngine;
  onToggle: (enabled: boolean) => void;
  onDelete?: () => void;
}) {
  return (
    <div
      className={cn("flex items-center justify-between px-4 py-3", !engine.enabled && "opacity-50")}
    >
      <div className="flex items-center gap-3">
        {engine.icon ? (
          <img
            src={engine.icon}
            alt=""
            className="size-5 rounded"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <div className="size-5 rounded bg-muted" />
        )}
        <div>
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium">{engine.label}</span>
            {engine.privacyUrl && (
              <a
                href={engine.privacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-600 dark:text-green-400"
              >
                <GlobeLock className="size-3" />
              </a>
            )}
            {engine.isCustom && (
              <span className="text-[10px] px-1 py-0.5 rounded bg-muted text-muted-foreground font-medium">
                Custom
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground font-mono truncate max-w-xs">{engine.url}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Switch checked={engine.enabled} onCheckedChange={onToggle} size="sm" />
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-destructive transition-colors"
          >
            <Trash2 className="size-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
